import { CapabilityRegistry } from '@trimble/web3d';
import {
  MissionPathRuns,
  PathGeometrySource,
  TrajectoryMetadataProvider,
} from '@trimble/web3d-providers';
import { LoadState } from '@trimble/data-loading';
import { Accessor, createMemo } from 'solid-js';
import { ProjectDataState } from '../data-state/DataStateModels';

export function createMissionPathRuns(
  dataState: ProjectDataState
): Accessor<readonly MissionPathRuns[]> {
  const missionPathRuns = createMemo(() => {
    const missions = dataState.mission;
    if (missions.state !== LoadState.LoadComplete) {
      return [];
    }
    return Object.values(missions.items).map((mission) => ({
      missionId: mission.missionTrn,
      pathId: mission.pathId ?? null,
      runs: mission.runs.map(({ runId, beginStation, endStation }) => ({
        runId,
        beginStation,
        endStation,
      })),
    }));
  });
  return missionPathRuns;
}

/**
 * Reads path geometry from the first pane that holds the path, the only copy
 * the index needs; releases the vertices from every pane, since the clones
 * share one array.
 */
export function createPathGeometrySource(
  registry: CapabilityRegistry
): PathGeometrySource {
  const providers = (): TrajectoryMetadataProvider[] =>
    registry.getPaneCapabilities(TrajectoryMetadataProvider);
  const holder = (pathId: string): TrajectoryMetadataProvider | undefined =>
    providers().find((p) => p.getById(pathId));

  return {
    metadata: (pathId) => holder(pathId)?.getById(pathId),
    release: (pathId) => {
      for (const provider of providers()) {
        provider.releaseSegmentVertices(pathId);
      }
    },
    restore: async (pathId) => {
      await holder(pathId)?.restoreSegmentVertices(pathId);
    },
  };
}
